"use client";

import { FormEvent, KeyboardEvent, useId, useRef, useState } from "react";
import { stegaClean } from "@sanity/client/stega";
import { usePostHog } from "posthog-js/react";
import { sendGTMEvent } from "@next/third-parties/google";
import { submitFormDataToZapier } from "@/helpers/zapier";
import { useAffiliateWebhook } from "@/components/global/AffiliateWebhookProvider";
import { cn } from "@/lib/utils";
import { IZapierForm } from "@/sanity/globals/ZapierForm";
import { IEliteWaitlist } from "@/sanity/pages/PageEliteTemplate";
import { ArrowRight, GREEN_BUTTON, SHEEN_ARROW, SheenLabel } from "./EliteShared";
import { EliteCopy } from "./copy";

/** The Elite waitlist Zap. A `zapierForm` linked in Sanity overrides it. */
const ELITE_WAITLIST_WEBHOOK = "https://hooks.zapier.com/hooks/catch/16907183/4mkew6a/";

type Field = "email" | "firstName";

/** Expo-out shared by every part of the reveal so the rows and the button move as one. */
const REVEAL = "duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none";

/**
 * Email + "Join The Waitlist". Row on desktop, stacked on mobile.
 * Focusing the email reveals the first-name field under it and, on desktop, slides the button from
 * beside the email down under both fields (Escape closes it again).
 * Positioned rather than re-flowed: a row→column switch cannot animate, `top` and `width` can.
 * `bordered` is the hero variant (the final CTA box has no border in Figma).
 */
export default function EliteWaitlistForm({
  waitlist,
  copy,
  bordered,
  className,
}: {
  waitlist: IEliteWaitlist;
  copy: EliteCopy;
  bordered?: boolean;
  className?: string;
}) {
  const postHog = usePostHog();
  const forwardAffiliate = useAffiliateWebhook();
  const emailId = useId();
  const firstNameId = useId();
  const messageId = useId();
  const firstNameRef = useRef<HTMLInputElement>(null);
  /** null until first opened, so the blur pulse does not play on load */
  const [revealed, setRevealed] = useState<boolean | null>(null);
  const [state, setState] = useState<"idle" | "sending" | "done">("idle");
  const [error, setError] = useState<Field | "submit" | null>(null);

  const placeholder = waitlist.placeholder ?? copy.emailPlaceholder;
  const firstNamePlaceholder = waitlist.firstNamePlaceholder ?? copy.firstNamePlaceholder;

  function reveal() {
    if (!revealed) setRevealed(true);
  }

  /** Escape: back to the one-line form. Typed values are kept. */
  function onKeyDown(e: KeyboardEvent<HTMLFormElement>) {
    if (e.key !== "Escape" || !revealed) return;
    setRevealed(false);
    setError(null);
    // otherwise the still-focused email would not fire onFocus to reopen it
    (e.target as HTMLElement).blur();
  }

  function clearError(field: Field) {
    if (error === field || error === "submit") setError(null);
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const elements = e.currentTarget.elements;
    const emailInput = elements.namedItem("email") as HTMLInputElement;
    const firstNameInput = elements.namedItem("firstName") as HTMLInputElement;
    const email = emailInput.value.trim();
    const firstName = firstNameInput.value.trim();

    if (!email || !emailInput.checkValidity()) {
      setError("email");
      emailInput.focus();
      return;
    }
    if (!firstName) {
      setError("firstName");
      setRevealed(true);
      // the field is out of the tab order until the reveal commits
      requestAnimationFrame(() => firstNameRef.current?.focus());
      return;
    }

    setError(null);
    setState("sending");

    const affiliateCode = forwardAffiliate
      ? new URLSearchParams(window.location.search).get("aff")
      : null;
    const fields: Record<string, string> = {
      firstName,
      email,
      ...(affiliateCode ? { affiliate_code: affiliateCode } : {}),
    };

    postHog?.identify(email, { firstName });
    postHog?.capture("form_submit", { form: "elite-waitlist", fields });

    const linked = waitlist.form ? stegaClean(waitlist.form) : undefined;
    const url = linked?.settings?.url || ELITE_WAITLIST_WEBHOOK;
    const res = await submitFormDataToZapier(fields, {
      ...linked,
      settings: { ...linked?.settings, url },
    } as IZapierForm);

    if (!res.success) {
      setState("idle");
      setError("submit");
      return;
    }

    sendGTMEvent({
      event: "zapierFormSubmit",
      "zapier-form-url": url,
      pageName: document.title,
      pagePath: window.location.href,
      "form-firstname": firstName,
      "form-email": email,
    });
    setState("done");
  }

  if (state === "done") {
    return (
      <p
        role="status"
        className={cn(
          "font-geist w-full max-w-[49.5rem] rounded-[1rem] bg-black/5 px-[2.5rem] py-[2.25rem] text-center text-[2.25rem] leading-[1.5] font-medium text-black",
          "animate-in fade-in zoom-in-95 duration-500",
          className,
        )}>
        {waitlist.successMessage ?? copy.successMessage}
      </p>
    );
  }

  const inputClass = (field: Field) =>
    cn(
      "font-geist h-[6.25rem] w-full rounded-[0.5rem] bg-white/80 px-[2.5rem] text-[2.25rem] text-black outline-none placeholder:text-black/60 lg:h-[6.6rem] lg:bg-transparent",
      "focus-visible:ring-2 focus-visible:ring-[#51a667]/50",
      error === field && "ring-2 ring-[#c0392b]/60",
    );

  return (
    <div className={cn("relative max-w-full", className)}>
      <form
        noValidate
        onSubmit={onSubmit}
        onKeyDown={onKeyDown}
        aria-busy={state === "sending"}
        className={cn(
          "overflow-hidden rounded-[1rem] bg-black/5 p-[0.625rem] backdrop-blur-[1.0625rem]",
          bordered && "lg:border lg:border-solid lg:border-black/[0.07]",
        )}>
        {/* desktop: fixed 75rem track (email 48.375 + gap 0.5 + button 26.125) the button travels in */}
        <div
          className={cn(
            "relative flex flex-col gap-[0.5rem] lg:block lg:w-[75rem]",
            // blurs then sharpens while the layout moves, over the same 0.7s
            revealed !== null && (revealed ? "animate-elite-blur-open" : "animate-elite-blur-close"),
            "motion-reduce:animate-none",
          )}>
          <div
            className={cn(
              "flex w-[48.375rem] max-w-full flex-col transition-[width,padding-bottom]",
              REVEAL,
              // room under the fields for the button once it has slid down (6.6rem + the 0.5rem gap)
              revealed ? "lg:w-[75rem] lg:pb-[7.1rem]" : "lg:w-[48.375rem] lg:pb-0",
            )}>
            <label
              htmlFor={emailId}
              className="sr-only">
              {placeholder}
            </label>
            <input
              id={emailId}
              name="email"
              type="email"
              required
              autoComplete="email"
              placeholder={placeholder}
              aria-invalid={error === "email"}
              aria-describedby={error ? messageId : undefined}
              onFocus={reveal}
              onInput={() => {
                reveal();
                clearError("email");
              }}
              className={inputClass("email")}
            />

            {/* 0fr → 1fr animates the row's real height, so everything below eases instead of jumping */}
            <div
              aria-hidden={!revealed}
              className={cn(
                "grid transition-[grid-template-rows]",
                REVEAL,
                revealed ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
              )}>
              <div className="min-h-0 overflow-hidden">
                <div
                  className={cn(
                    "pt-[0.5rem] transition-[opacity,transform] lg:pt-0",
                    REVEAL,
                    revealed ? "translate-y-0 opacity-100 delay-150" : "-translate-y-[1.5rem] opacity-0",
                  )}>
                  <label
                    htmlFor={firstNameId}
                    className="sr-only">
                    {firstNamePlaceholder}
                  </label>
                  <input
                    ref={firstNameRef}
                    tabIndex={revealed ? undefined : -1}
                    id={firstNameId}
                    name="firstName"
                    type="text"
                    required
                    maxLength={80}
                    autoComplete="given-name"
                    placeholder={firstNamePlaceholder}
                    aria-invalid={error === "firstName"}
                    aria-describedby={error ? messageId : undefined}
                    onInput={() => clearError("firstName")}
                    className={cn(
                      inputClass("firstName"),
                      // desktop inputs are transparent: a hairline separates the two rows
                      "lg:rounded-t-none lg:border-t lg:border-solid lg:border-black/[0.07]",
                      // the collapsing wrapper clips outer rings
                      "ring-inset",
                    )}
                  />
                </div>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={state === "sending"}
            className={cn(
              GREEN_BUTTON,
              "font-geist flex h-[6.25rem] w-full shrink-0 items-center justify-center gap-[1.25rem] rounded-[0.5rem] text-[2rem] font-semibold whitespace-nowrap text-black disabled:opacity-70 lg:h-[6.6rem]",
              // pinned to the right edge, so it grows leftwards while it slides down under the fields
              "lg:absolute lg:right-0 lg:transition-[top,width]",
              REVEAL,
              revealed ? "lg:top-[13.7rem] lg:w-[75rem]" : "lg:top-0 lg:w-[26.125rem]",
            )}>
            <SheenLabel>{waitlist.buttonText ?? copy.joinWaitlist}</SheenLabel>
            <ArrowRight className={cn("h-[1.5rem] w-[1.75rem]", SHEEN_ARROW)} />
          </button>
        </div>
      </form>

      {/* absolute so a message never shifts the section */}
      <p
        id={messageId}
        role="alert"
        className="font-geist absolute inset-x-0 top-full mt-[1.25rem] text-center text-[1.75rem] text-[#c0392b]">
        {error ? copy.errors[error] : null}
      </p>
    </div>
  );
}
